import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import { mutateData } from "@/core/api/api-methods";
import {
  invalidateAfterMutation,
  payrollBatchCacheKeys,
} from "@/core/api/invalidate-after-mutation";
import { logger } from "@/core/utils/func/logger";
import { payrollEndpoints } from "@/features/payroll/api/endpoints";
import type { PayrollBatchCancelResponseDto } from "@/features/payroll/api/dto";

/**
 * Annule une paie `DRAFT` ou `SIMULATED` (`POST /PayrollBatches/{id}/cancel`).
 * Une paie lancée ne s'annule pas. Codes : 200 ; 404 ; 422.
 */
export const useCancelPayrollBatch = (batchId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["payroll", "payrollBatches", "cancel", batchId],
    mutationFn: () =>
      mutateData({
        url: payrollEndpoints.payrollBatches.cancel(batchId),
        method: "POST",
      }) as unknown as Promise<PayrollBatchCancelResponseDto>,
    onSuccess: () =>
      invalidateAfterMutation(queryClient, payrollBatchCacheKeys(batchId)),
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useCancelPayrollBatch failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "payroll" }
      );
    },
  });
};
